/**
 * @file Transactions.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React from "react";
import SkinPage from "../../SkinPage";
import Widget from "../../widgets/Widget";
import { LineChart, Line, XAxis, Tooltip } from "recharts";
import { formatAmount } from "../../../../utils/formatAmount";

const CustomTooltipp = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <span className="label">
          {`${label}`}{" "}
          <strong>${`${formatAmount(payload[0].value, 2)}`}</strong>
        </span>
      </div>
    );
  }

  return null;
};

function Transactions() {
  const data = [
    {
      month: "JAN",
      Balance: 85000,
    },
    {
      month: "FEB",
      Balance: 120000,
    },
    {
      month: "MAR",
      Balance: 280000,
    },
    {
      month: "APR",
      Balance: 140000,
    },
    {
      month: "MAY",
      Balance: 50000,
    },
    {
      month: "JUN",
      Balance: 170000,
    },
    {
      month: "JUL",
      Balance: 280000,
    },
    {
      month: "AUG",
      Balance: 180000,
    },
    {
      month: "SEP",
      Balance: 100000,
    },
    {
      month: "OCT",
      Balance: 280000,
    },
    {
      month: "NOV",
      Balance: 130000,
    },
    {
      month: "DEC",
      Balance: 280000,
    },
  ];

  return (
    <SkinPage>
      <Widget name={"transactions"}>
        <div className="heading">
          <h1 className="title">Transactions History</h1>
        </div>
        <div className="available-balance">
          <p className="subtitle">Available Balance:</p>
          <h2 className="amount text-left">${formatAmount(980840.95, 2)}</h2>
          <p className="growth">Growth: +26%</p>
        </div>
        <div className="chart">
          <LineChart width={1000} height={100} data={data}>
            <XAxis dataKey="month" stroke="#95B2C5" strokeWidth={0} />
            <Tooltip content={<CustomTooltipp />} />
            <Line
              type="monotone"
              dataKey="Balance"
              stroke="#ABECD6"
              strokeWidth={2}
            />
          </LineChart>
        </div>
        <div className="transactions-table">
          <table>
            <thead>
              <th>Transaction</th>
              <th>Date</th>
              <th>Status</th>
              <th>Type</th>
              <th>Ticker</th>
              <th>Price</th>
              <th>Fees</th>
              <th>Total</th>
            </thead>
            <tbody>
              <tr>
                <td>Fidelity Contrafund</td>
                <td>05/22/2023</td>
                <td>
                  <span className="completed">Completed</span>
                </td>
                <td>Sell</td>
                <td>FCNTX</td>
                <td>${formatAmount(100, 2)}</td>
                <td>${formatAmount(10, 2)}</td>
                <td>${formatAmount(110, 2)}</td>
              </tr>
              <tr>
                <td>Fidelity Contrafund</td>
                <td>05/22/2023</td>
                <td>
                  <span className="completed">Completed</span>
                </td>
                <td>Sell</td>
                <td>FCNTX</td>
                <td>${formatAmount(200, 2)}</td>
                <td>${formatAmount(20, 2)}</td>
                <td>${formatAmount(220, 2)}</td>
              </tr>
              <tr>
                <td>Fidelity Contrafund</td>
                <td>05/22/2023</td>
                <td>
                  <span className="completed">Completed</span>
                </td>
                <td>Sell</td>
                <td>FCNTX</td>
                <td>${formatAmount(300, 2)}</td>
                <td>${formatAmount(30, 2)}</td>
                <td>${formatAmount(330, 2)}</td>
              </tr>
            </tbody>
          </table>
          <div className="view-all">
            <span className="cursor-pointer">View all transactions</span>
          </div>
        </div>
      </Widget>
    </SkinPage>
  );
}

export default Transactions;
